import { useEffect, useState, useCallback } from 'react'
import { getToken, isSupported, onMessage } from 'firebase/messaging'
import { auth, messaging, db } from '../firebase'
import { doc, setDoc } from 'firebase/firestore'
import { toast } from 'react-toastify'

export const useNotifications = () => {
  const [permission, setPermission] = useState<NotificationPermission>(
    Notification.permission
  )
  const [loading, setLoading] = useState(false)

  const showToast = useCallback(
    (
      message: string,
      type: 'success' | 'error' | 'info' | 'warning' = 'info'
    ) => {
      toast[type](message, {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      })
    },
    []
  )

  const enableNotifications = useCallback(async () => {
    if (permission === 'granted') {
      showToast('Notifications are already enabled', 'info')
      return
    }

    try {
      setLoading(true)
      const isMessagingSupported = await isSupported()
      if (!isMessagingSupported) {
        showToast('Push notifications not supported in this browser', 'warning')
        return console.warn('Firebase Messaging not supported')
      }

      const registration = await navigator.serviceWorker.ready
      const perm = await Notification.requestPermission()
      setPermission(perm)

      if (perm !== 'granted') {
        showToast('Notification permission denied', 'error')
        return console.warn('🚫 Notification permission denied')
      }

      const fcmToken = await getToken(messaging, {
        vapidKey: import.meta.env.VITE_VAPID_API_KEY,
        serviceWorkerRegistration: registration,
      })
      console.log(fcmToken)

      const user = auth.currentUser
      if (user && fcmToken) {
        await setDoc(
          doc(db, 'userProfiles', user.uid),
          { tokens: [fcmToken] },
          { merge: true }
        )
        showToast('Notifications enabled successfully!', 'success')
        console.log('✅ Token saved to userProfiles')
      } else {
        showToast('Please sign in to enable notifications', 'warning')
        console.warn('❌ No authenticated user or no FCM token')
      }
    } catch (error) {
      showToast('Failed to enable notifications', 'error')
      console.error('Notification initialization failed:', error)
    } finally {
      setLoading(false)
    }
  }, [permission, showToast])

  useEffect(() => {
    setPermission(Notification.permission)
  }, [])

  useEffect(() => {
    const unsubscribe = onMessage(messaging, (payload) => {
      console.log('Foreground message received:', payload)

      // Show toast notification for foreground messages
      const title =
        payload.notification?.title || payload.data?.title || 'New Message'
      const body = payload.notification?.body || payload.data?.body || ''

      showToast(`${title}: ${body}`, 'info')

      // Also show browser notification if permission granted
      if (Notification.permission === 'granted') {
        new Notification(title, {
          body: body,
          icon: '/andyanime.png',
        })
      }
    })

    return () => unsubscribe()
  }, [showToast])

  return { permission, enableNotifications, loading, showToast }
}
